/**
 * ModeToggle — reusable two-state toggle for explainer diagrams.
 *
 * Extracts the repeated "flip between two diagram modes" pattern used by
 * BeckChevalleyExplainer, FibrationExplainer, and future structure explainers.
 * Each consumer passes labels, the diagram renderer, and an accent color.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useState } from "react";
import { FONT, GRAY, withAlpha } from "../../design-system/tokens.js";

export interface ModeToggleOption<M extends string> {
  readonly mode: M;
  readonly label: string;
}

export interface ModeToggleProps<M extends string> {
  /** The two (or more) mode options with display labels. */
  readonly options: readonly ModeToggleOption<M>[];
  /** Which mode to show initially. */
  readonly defaultMode: M;
  /** Accent color for the active button. */
  readonly accent: string;
  /** Accessible group label. */
  readonly ariaLabel: string;
  /** Render the diagram for the currently selected mode. */
  readonly children: (mode: M) => React.ReactNode;
  /** Optional caption rendered below the diagram, receiving the active mode. */
  readonly caption?: (mode: M) => string;
}

/**
 * Generic two-state (or N-state) toggle for inline diagram panels.
 * Keeps diagram mode selection DRY across all structure explainers.
 */
export function ModeToggle<M extends string>({
  options,
  defaultMode,
  accent,
  ariaLabel,
  children,
  caption,
}: ModeToggleProps<M>): React.JSX.Element {
  const [mode, setMode] = useState<M>(defaultMode);
  const select = useCallback(
    (m: M): (() => void) =>
      () => {
        setMode(m);
      },
    [],
  );

  return (
    <div style={{ display: "grid", gap: "0.5rem" }}>
      <div
        role="group"
        aria-label={ariaLabel}
        style={{ display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap" }}
      >
        {options.map((opt) => (
          <button
            key={opt.mode}
            type="button"
            onClick={select(opt.mode)}
            aria-pressed={mode === opt.mode}
            style={{
              appearance: "none",
              border: mode === opt.mode ? `2px solid ${accent}` : `1px solid ${GRAY[300]}`,
              background: mode === opt.mode ? withAlpha(accent, "1A") : "white",
              borderRadius: "0.4rem",
              padding: "0.3rem 0.7rem",
              cursor: "pointer",
              fontSize: "0.8rem",
              color: mode === opt.mode ? accent : GRAY[700],
              fontWeight: 600,
              fontFamily: FONT.body,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {children(mode)}
      {caption && (
        <p
          style={{
            margin: "0.5rem 0 0",
            fontSize: "0.8rem",
            color: GRAY[600],
            textAlign: "center",
            fontStyle: "italic",
          }}
        >
          {caption(mode)}
        </p>
      )}
    </div>
  );
}
